import { useEffect, useState } from "react";
import {
  Building2,
  CheckCircle2,
  Filter,
  Search,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import {
  createProspectCampaign,
  enrichProspect,
  checkProspectEnrichment,
  getProspectCampaigns,
  getProspectDiscoveries,
  getProspectIssues,
  getProspectScores,
  getProspectProviderStatus,
  startProspectDiscovery,
  pollLatestProspectDiscovery,
  rescanProspect,
  reviewProspect,
} from "./lib/api";
import type { ProspectCampaign, ProspectDiscovery } from "./types";
import type{CrmRole}from'./lib/crmRbac';
import "./ProspectingLegacy.css";
import {prospectIndustries,filterProspectBusinessRecords} from "./lib/prospecting";
import {invalidateCrmData} from "./lib/crmSync";
const initial = {
  name: "",
  industry: "",
  geography: "",
  city: "",
  state: "",
  postalCode: "",
  keywords: "",
  websiteRequired: true,
  decisionMakerRequired: false,
  minimumScore: 60,
  dailyDiscoveryLimit: 25,
  dailyScanLimit: 25,
  dailyContactLimit: 10,
};
export function ProspectingLegacy({role='BDR_SDR',excludedProspectIds=[],excludedCampaignIds=[]}:{role?:CrmRole;excludedProspectIds?:string[];excludedCampaignIds?:string[]}) {
  const [allCampaigns, setCampaigns] = useState<ProspectCampaign[]>([]),
    [allProspects, setProspects] = useState<ProspectDiscovery[]>([]),
    [scores, setScores] = useState<Record<string,{score:number;reason:string;confidence:number}>>({}),
    [issues, setIssues] = useState<Record<string,{title:string;severity:string}>>({}),
    [providerState, setProviderState] = useState<'CREDENTIALS_REQUIRED'|'CONFIGURED_NOT_VERIFIED'>('CREDENTIALS_REQUIRED'),
    [form, setForm] = useState(initial),
    [editingId, setEditingId] = useState<string|null>(null),
    [open, setOpen] = useState(false),
    [state, setState] = useState<"idle" | "saving" | "success" | "error">(
      "idle",
    ),
    [error, setError] = useState("");
  const campaigns=filterProspectBusinessRecords(allCampaigns,excludedCampaignIds),prospects=filterProspectBusinessRecords(allProspects,excludedProspectIds);
  async function act(prospectId:string,action:'REVIEW'|'APPROVE'|'REJECT'|'SUPPRESS'|'RESCAN'|'ENRICH'|'CHECK_ENRICH'){
    if(excludedProspectIds.includes(prospectId))return;
    setState('saving');setError('');
    try{if(action==='ENRICH'||action==='CHECK_ENRICH'){const result=await (action==='ENRICH'?enrichProspect(prospectId):checkProspectEnrichment(prospectId));if(result.writesPerformed)invalidateCrmData();setError(result.state==='PENDING'?'An approved bounded enrichment task is processing.':result.state==='COMPLETE'?'Cached provider evidence is available in company details; identities and direct phone access remain unverified.':'No completed enrichment result is available. Checking status starts no paid provider task.')}else if(action==='RESCAN')await rescanProspect(prospectId);else await reviewProspect(prospectId,action);setProspects(await getProspectDiscoveries());setState('success')}
    catch(value){setState('error');setError(value instanceof Error?value.message.replaceAll('_',' '):'Prospect action failed.')}
  }
  useEffect(() => {
    Promise.all([getProspectCampaigns(), getProspectDiscoveries(), getProspectScores(), getProspectIssues()])
      .then(([a, b, scoreRows, issueRows]) => {
        setCampaigns(a);
        setProspects(b);
        setScores(Object.fromEntries(scoreRows.map((row)=>[row.prospectId,row])));
        setIssues(Object.fromEntries(issueRows.map((row)=>[row.prospectId,{title:row.title,severity:row.severity}])));
      })
      .catch(() => setError("Prospecting data is temporarily unavailable."));
    getProspectProviderStatus().then((provider)=>setProviderState(provider.state)).catch(()=>setProviderState('CREDENTIALS_REQUIRED'));
  }, []);
  async function save() {
    if(editingId&&excludedCampaignIds.includes(editingId))return;
    setState("saving");
    setError("");
    try {
      await createProspectCampaign({
        campaignId: editingId || crypto.randomUUID(),
        name: form.name,
        filters: {
          industry: form.industry,
          geography: form.geography,
          city: form.city,
          state: form.state,
          postalCode: form.postalCode,
          websiteRequired: form.websiteRequired,
          decisionMakerRequired: form.decisionMakerRequired,
          keywords: form.keywords
            .split(",")
            .map((x) => x.trim())
            .filter(Boolean),
          technologies: [],
          minimumScore: form.minimumScore,
          dailyDiscoveryLimit: form.dailyDiscoveryLimit,
          dailyScanLimit: form.dailyScanLimit,
          dailyContactLimit: form.dailyContactLimit,
        },
        assignedUserId: null,
        status: editingId ? campaigns.find((campaign)=>campaign.id===editingId)?.status || "DRAFT" : "DRAFT",
      });
      setCampaigns(await getProspectCampaigns());
      setOpen(false);
      setEditingId(null);
      setForm(initial);
      setState("success");
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message.replaceAll("_", " ")
          : "Campaign could not be saved.",
      );
      setState("error");
    }
  }
  function campaignFilters(campaign:ProspectCampaign){return {industry:campaign.industry||'',businessCategory:campaign.businessCategory||'',geography:campaign.geography||'',city:campaign.city||'',state:campaign.state||'',postalCode:campaign.postalCode||'',radiusMiles:campaign.radiusMiles||'',websiteRequired:true,decisionMakerRequired:false,keywords:campaign.keywords||[],technologies:campaign.technologies||[],minimumScore:campaign.minimumOpportunityScore,dailyDiscoveryLimit:campaign.dailyDiscoveryLimit,dailyScanLimit:campaign.dailyScanLimit,dailyContactLimit:campaign.dailyContactLimit}}
  async function setCampaignStatus(campaign:ProspectCampaign,status:'ACTIVE'|'PAUSED'|'ARCHIVED'){
    if(excludedCampaignIds.includes(campaign.id))return;
    setState('saving');setError('');try{await createProspectCampaign({campaignId:campaign.id,name:campaign.name,filters:campaignFilters(campaign),assignedUserId:campaign.assignedUserId||null,status});setCampaigns(await getProspectCampaigns());setState('success')}catch(value){setState('error');setError(value instanceof Error?value.message.replaceAll('_',' '):'Campaign could not be updated.')}
  }
  function editCampaign(campaign:ProspectCampaign){if(excludedCampaignIds.includes(campaign.id))return;setEditingId(campaign.id);setForm({...initial,name:campaign.name,industry:campaign.industry||campaign.businessCategory||'',geography:campaign.geography||'',city:campaign.city||'',state:campaign.state||'',postalCode:campaign.postalCode||'',keywords:(campaign.keywords||[]).join(', '),minimumScore:campaign.minimumOpportunityScore,dailyDiscoveryLimit:campaign.dailyDiscoveryLimit,dailyScanLimit:campaign.dailyScanLimit,dailyContactLimit:campaign.dailyContactLimit});setOpen(true)}
  async function discover(campaign:ProspectCampaign){if(excludedCampaignIds.includes(campaign.id))return;setState('saving');setError('');try{const result=await startProspectDiscovery(campaign.id,Math.min(campaign.dailyDiscoveryLimit,50),crypto.randomUUID());setError(result.state==='PENDING'?'Outscraper accepted the bounded task. Refresh after provider completion.':`Discovery completed: ${result.created||0} new prospects.`);setProspects(await getProspectDiscoveries());setState('success')}catch(value){setState('error');setError(value instanceof Error?value.message.replaceAll('_',' '):'Discovery could not start.')}}
  async function pollDiscovery(campaign:ProspectCampaign){if(excludedCampaignIds.includes(campaign.id))return;setState('saving');setError('');try{const result=await pollLatestProspectDiscovery(campaign.id);setError(result.state==='PENDING'?'Provider task is still processing.':`Discovery completed: ${result.created||0} new prospects.`);setProspects(await getProspectDiscoveries());setState('success')}catch(value){setState('error');setError(value instanceof Error?value.message.replaceAll('_',' '):'Provider task could not be checked.')}}
  return (
    <div className="prospecting-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">PROSPECTING</span>
          <h1>Evidence-first prospect discovery</h1>
          <p>
            Discover organizations, deduplicate against CRM truth, run public
            AvertSignal visibility checks, score evidence, and place qualified
            prospects into review.
          </p>
        </div>
        <button className="primary-btn" onClick={() => setOpen(true)}>
          <Sparkles size={16} />
          New campaign
        </button>
      </div>
      <div className="prospect-truth">
        <ShieldCheck size={18} />
        <div>
          <strong>Review first</strong>
          <span>
            Discovery never starts automatic email or SMS outreach. Suppression,
            eligibility, and administrator approval remain mandatory.
          </span>
        </div>
        <b>{role==='SUPER_ADMIN'?(providerState==='CREDENTIALS_REQUIRED'?'Provider setup required':'Provider configured · live acceptance pending'):'Prospect discovery · Ready for review'}</b>
      </div>
      {error && (
        <div className="runtime-banner degraded" role="alert">
          {error}
        </div>
      )}
      <p className="prospect-muted">Business discovery and review totals exclude positively labeled tests. Retained plans include audit context; one test member never classifies a whole mixed campaign as a test.</p>
      <section className="prospect-kpis">
        <article>
          <strong>{campaigns.length}</strong>
          <span>Retained plans · audit included</span>
        </article>
        <article>
          <strong>{prospects.length}</strong>
          <span>Discovered</span>
        </article>
        <article>
          <strong>
            {prospects.filter((x) => x.status === "REVIEW").length}
          </strong>
          <span>Review queue</span>
        </article>
        <article>
          <strong>0</strong>
          <span>Auto outreach</span>
        </article>
      </section>
      <section className="panel">
        <div className="panel-header"><div><span>CAMPAIGNS</span><h3>Review-first discovery plans</h3></div></div>
        {campaigns.length?<div className="prospect-list">{campaigns.map(c=><article key={c.id}><Building2 size={17}/><div><strong>{c.name}</strong><span>{c.industry||c.businessCategory||'Business type pending'} · {[c.city,c.state,c.postalCode,c.geography].filter(Boolean).join(', ')||'Location pending'}</span><span>Limit {Math.min(c.dailyDiscoveryLimit,50)} · Minimum score {c.minimumOpportunityScore} · Review first</span></div><b>{c.status}</b><div className="prospect-actions"><button type="button" disabled={state==='saving'||c.status==='ARCHIVED'} onClick={()=>editCampaign(c)}>Edit</button><button type="button" disabled={state==='saving'||c.status==='ARCHIVED'} onClick={()=>void setCampaignStatus(c,c.status==='PAUSED'?'ACTIVE':'PAUSED')}>{c.status==='PAUSED'?'Resume':'Pause'}</button><button type="button" disabled={state==='saving'||c.status==='ARCHIVED'} onClick={()=>void setCampaignStatus(c,'ARCHIVED')}>Archive</button><button type="button" disabled={state==='saving'||c.status==='ARCHIVED'} onClick={()=>void discover(c)}>Run discovery</button><button type="button" disabled={state==='saving'||c.status==='ARCHIVED'} onClick={()=>void pollDiscovery(c)}>Check provider result</button></div></article>)}</div>:<div className="empty-state"><strong>No campaigns yet</strong><span>Create a bounded review-first campaign.</span></div>}
      </section>
      <section className="panel">
        <div className="panel-header">
          <div>
            <span>REVIEW QUEUE</span>
            <h3>Qualified prospect evidence</h3>
          </div>
          <span>ORGANIZATION FIRST</span>
        </div>
        {prospects.length ? (
          <div className="prospect-list">
            {prospects.map((p) => (
              <article key={p.id}>
                <Building2 size={17} />
                <div>
                  <strong>{p.companyName}</strong>
                  <span>{p.location || "Location unavailable"} · {p.industry || "Industry unclassified"}</span>
                  <span>{p.domain || "Website unavailable"} · {p.decisionMakerName ? `${p.decisionMakerName}${p.decisionMakerRole ? ` · ${p.decisionMakerRole}` : ""}` : "Contact enrichment incomplete"}</span>
                  <span>{scores[p.id] ? `Opportunity score ${scores[p.id].score}/100 · ${scores[p.id].reason}` : "Opportunity score pending evidence"}</span>
                  <span>{issues[p.id] ? `Top issue: ${issues[p.id].severity} · ${issues[p.id].title}` : "No observed issue recorded"}</span>
                </div>
                <b>{p.status}</b>
                <div className="prospect-actions"><button type="button" onClick={()=>void act(p.id,'REVIEW')}>Review</button><button type="button" onClick={()=>void act(p.id,'APPROVE')} disabled={p.status!=="REVIEW"||state==='saving'}>Approve for Sales</button><button type="button" onClick={()=>void act(p.id,'REJECT')}>Reject</button><button type="button" onClick={()=>void act(p.id,'SUPPRESS')}>Suppress</button><button type="button" onClick={()=>void act(p.id,'RESCAN')}>Re-scan</button><button type="button" disabled={state==='saving'} onClick={()=>void act(p.id,'ENRICH')}>Review enrichment</button><button type="button" disabled={state==='saving'} onClick={()=>void act(p.id,'CHECK_ENRICH')}>Check enrichment result</button></div>
              </article>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <Search />
            <strong>No prospects discovered yet</strong>
            <span>
              Create a review-first campaign. Provider discovery activates only
              after its server credential is configured.
            </span>
          </div>
        )}
      </section>
      {open && (
        <div className="record-modal-backdrop">
          <form
            className="record-modal prospect-campaign"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <div className="record-modal-head">
              <div>
                <span>PROSPECT DISCOVERY</span>
                <h2>{editingId?'Edit review-first campaign':'New review-first campaign'}</h2>
              </div>
              <button type="button" onClick={() => {setOpen(false);setEditingId(null)}}>
                ×
              </button>
            </div>
            <div className="record-form-grid">
              <label className="wide">
                Campaign name
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </label>
              <label>
                Business type / industry
                <datalist id="prospect-industry-categories">{prospectIndustries.map(industry=><option key={industry.key} value={industry.key}>{industry.name}</option>)}</datalist>
                <input list="prospect-industry-categories"
                  required
                  value={form.industry}
                  onChange={(e) =>
                    setForm({ ...form, industry: e.target.value })
                  }
                />
              </label>
              <label>
                Geography
                <input
                  value={form.geography}
                  onChange={(e) =>
                    setForm({ ...form, geography: e.target.value })
                  }
                />
              </label>
              <label>
                City
                <input
                  value={form.city}
                  onChange={(e) =>
                    setForm({ ...form, city: e.target.value })
                  }
                />
              </label>
              <label>State<input value={form.state} onChange={(e)=>setForm({...form,state:e.target.value})}/></label>
              <label>ZIP<input value={form.postalCode} onChange={(e)=>setForm({...form,postalCode:e.target.value})}/></label>
              <label>
                Minimum score
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.minimumScore}
                  onChange={(e) =>
                    setForm({ ...form, minimumScore: Number(e.target.value) })
                  }
                />
              </label>
              <label className="checkbox-line"><input type="checkbox" checked={form.websiteRequired} onChange={(e)=>setForm({...form,websiteRequired:e.target.checked})}/>Website required</label>
              <label className="checkbox-line"><input type="checkbox" checked={form.decisionMakerRequired} onChange={(e)=>setForm({...form,decisionMakerRequired:e.target.checked})}/>Decision maker required</label>
              <label className="wide">
                Keywords
                <input
                  value={form.keywords}
                  onChange={(e) =>
                    setForm({ ...form, keywords: e.target.value })
                  }
                  placeholder="local services, multi-location"
                />
              </label>
              <label>
                Daily discovery limit
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={form.dailyDiscoveryLimit}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      dailyDiscoveryLimit: Number(e.target.value),
                    })
                  }
                />
              </label>
              <label>
                Daily scan limit
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={form.dailyScanLimit}
                  onChange={(e) =>
                    setForm({ ...form, dailyScanLimit: Number(e.target.value) })
                  }
                />
              </label>
            </div>
            <div className="prospect-review-law">
              <Filter size={16} />
              <span>
                Approval mode is fixed to <b>Review first</b>. No communication
                is sent. Paid discovery remains blocked until a bounded cost allowance is configured.
              </span>
            </div>
            <div className="record-modal-actions">
              <button type="button" onClick={() => {setOpen(false);setEditingId(null)}}>
                Cancel
              </button>
              <button className="primary-btn" disabled={state === "saving"}>
                <CheckCircle2 size={15} />
                {state === "saving" ? "Saving…" : editingId ? "Save campaign" : "Create campaign"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
